// Accordion labels switched to sentence case (acronyms kept).
import React, { FC, useEffect } from 'react';

import { NativeResources } from '@ariksa/inventory-core';
import { Accordion, Box, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useNetworkResourceMaliciousEgress,
  useNetworkServiceResourcePortUsage,
} from 'services/Network/useNetworkService';

import { WithResourceIcon } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { TableColumnProps } from 'components/DataDisplay/NewTable/types';
import { IconTypes } from 'components/Icons/types';
import { MetaAccordionItem } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/Components/MetaAccordionItem';
import { InternetAccessMapWrapper } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Network/InternetAccessMapWrapper';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { formatDate } from 'utils/date';

export const VPCNetworkTab: FC = () => {
  const {
    resourceUuid,
    resourceAccountId,
    resourceType,
    network,
  } = useSelector(selectActiveResource);
  const { resourcePortUsage } = useNetworkServiceResourcePortUsage();
  const { resourceMaliciousEgress } = useNetworkResourceMaliciousEgress(
    resourceAccountId,
    resourceUuid,
  );
  const { openPorts } = network;
  const dispatch = useDispatch();

  useEffect(() => {
    resourcePortUsage.sync({
      resourceId: resourceUuid,
      accountId: resourceAccountId,
    });
  }, [resourceAccountId, resourcePortUsage, resourceUuid]);

  useEffect(() => {
    if (
      resourceType === NativeResources.Ec2Instance ||
      resourceType === NativeResources.AzureVirtualMachine ||
      resourceType === NativeResources.GcpComputeEngine
    )
      dispatch(actions.getOpenPorts({ q: { resourceId: resourceUuid } }));
  }, [dispatch, resourceUuid, resourceType]);

  const columns = (
    resourceType: any = NativeResources.Subnet,
  ): TableColumnProps[] => [
    {
      header: <Box pl="thLeftPaddingWithIcon">Source IP</Box>,
      accessor: 'cidr',
      render: ({ value }) => {
        return (
          <WithResourceIcon resourceType={resourceType}>
            <Box>{value}</Box>
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Port',
      accessor: 'port',
    },
    {
      header: 'Protocol',
      accessor: 'protocol',
    },
    {
      header: 'Type',
      accessor: 'type',
    },
    {
      header: 'Last Timestamp',
      accessor: 'lastTimestamp',
      render: ({ value }) => value,
    },
  ];

  const egressColumns: TableColumnProps[] = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Destination IP</Box>,
      accessor: 'destination_ip',
      render: ({ value }) => {
        return (
          <WithResourceIcon resourceType={'Internet'}>
            <Box>{value}</Box>
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Destination Port',
      accessor: 'destination_port',
    },
    {
      header: 'Protocol',
      accessor: 'protocol',
    },
    {
      header: 'No. of Sources',
      accessor: 'sources',
    },
    {
      header: 'Last Timestamp',
      accessor: 'last_timestamp',
      render: ({ value }) => {
        return formatDate(value);
      },
    },
  ];

  return (
    <Stack w={'full'} h={'full'} pb={5} spacing={8}>
      <Box>
        <Accordion allowMultiple defaultIndex={[0]}>
          {(resourceType === NativeResources.Ec2Instance ||
            resourceType === NativeResources.AzureVirtualMachine ||
            resourceType === NativeResources.GcpComputeEngine) && (
            <MetaAccordionItem
              panel={
                <Table
                  columns={[
                    {
                      header: 'CIDR',
                      accessor: 'cidr',
                    },
                    {
                      header: 'Protocol',
                      accessor: 'protocol',
                    },
                    {
                      header: 'Port Range',
                      render: ({ row }) =>
                        row?.start_port + ' - ' + row?.end_port,
                    },
                  ]}
                  isLoading={openPorts.isLoading}
                  data={openPorts.data}
                  noDataMessage="No open ports"
                />
              }
              iconType={IconTypes.Port}
              label="Ingress open ports"
              height="400px"
              totalCount={openPorts.data?.length ?? 0}
            />
          )}
          <MetaAccordionItem
            iconType={IconTypes.Egress}
            label={'Malicious egress traffic from virtual machine'}
            panel={
              <Table
                columns={egressColumns}
                data={resourceMaliciousEgress.data ?? []}
              />
            }
            totalCount={resourceMaliciousEgress.data?.length ?? 0}
            iconBGColor="critical"
          />
          <MetaAccordionItem
            iconType={IconTypes.Internet}
            label={'Ingress traffic from internet'}
            panel={
              <Table
                columns={columns('Internet')}
                data={resourcePortUsage.data?.internet ?? []}
              />
            }
            totalCount={resourcePortUsage.data?.internet?.length ?? 0}
          />
          <MetaAccordionItem
            iconType={NativeResources.VirtualPrivateCloud}
            label={'Ingress traffic from virtual private cloud (VPC)'}
            panel={
              <Table
                columns={columns()}
                data={resourcePortUsage.data?.vpc ?? []}
              />
            }
            totalCount={resourcePortUsage.data?.vpc?.length ?? 0}
          />
          <MetaAccordionItem
            iconType={IconTypes.NetworkMap}
            label={'Network map'}
            panel={<InternetAccessMapWrapper />}
            iconBGColor="Network"
            height="570px"
          />
        </Accordion>
      </Box>
    </Stack>
  );
};
